/**
 * RTWO Cryptography & Identity Engine — Repositories
 *
 * In-memory stores remain empty until Key Provider and Identity Backend ingest data.
 */

import type {
  AddressHex,
  AddressType,
  IdentityId,
  IdentityType,
  KeyPairId,
  KeyStatus,
  PolicyId,
  RTWOAddress,
  RTWOIdentity,
  RTWOKeyPair,
  RTWOKeyStoragePolicy,
  RTWOSignature,
  RTWOTrustRecord,
  SignatureId,
  SignatureStatus,
  TrustRecordId,
} from '../domain-models';
import type { CryptoEvent } from '../domain-models';

class InMemoryMapRepository<T> {
  private readonly store = new Map<string, T>();

  constructor(private readonly getKey: (entity: T) => string) {}

  findAll(): T[] {
    return Array.from(this.store.values());
  }

  findById(id: string): T | null {
    return this.store.get(id) ?? null;
  }

  save(entity: T): void {
    this.store.set(this.getKey(entity), entity);
  }

  delete(id: string): boolean {
    return this.store.delete(id);
  }

  count(): number {
    return this.store.size;
  }
}

export class KeyPairRepository {
  private readonly byId = new InMemoryMapRepository<RTWOKeyPair>((keyPair) => keyPair.keyPairId);

  findAll(): RTWOKeyPair[] {
    return this.byId.findAll();
  }

  findById(keyPairId: KeyPairId): RTWOKeyPair | null {
    return this.byId.findById(keyPairId);
  }

  findByStatus(status: KeyStatus): RTWOKeyPair[] {
    return this.findAll().filter((keyPair) => keyPair.status === status);
  }

  save(keyPair: RTWOKeyPair): void {
    this.byId.save(keyPair);
  }

  count(): number {
    return this.byId.count();
  }
}

export class AddressRepository {
  private readonly byAddress = new InMemoryMapRepository<RTWOAddress>(
    (entry) => entry.address ?? `pending-${entry.addressType}`
  );

  findAll(): RTWOAddress[] {
    return this.byAddress.findAll();
  }

  findByAddress(address: AddressHex): RTWOAddress | null {
    return this.byAddress.findById(address);
  }

  findByType(addressType: AddressType): RTWOAddress[] {
    return this.findAll().filter((entry) => entry.addressType === addressType);
  }

  save(address: RTWOAddress): void {
    this.byAddress.save(address);
  }

  count(): number {
    return this.byAddress.count();
  }
}

export class SignatureRepository {
  private readonly byId = new InMemoryMapRepository<RTWOSignature>(
    (signature) => signature.signatureId
  );

  findAll(): RTWOSignature[] {
    return this.byId.findAll();
  }

  findById(signatureId: SignatureId): RTWOSignature | null {
    return this.byId.findById(signatureId);
  }

  findByStatus(status: SignatureStatus): RTWOSignature[] {
    return this.findAll().filter((signature) => signature.status === status);
  }

  save(signature: RTWOSignature): void {
    this.byId.save(signature);
  }

  count(): number {
    return this.byId.count();
  }
}

export class IdentityRepository {
  private readonly byId = new InMemoryMapRepository<RTWOIdentity>(
    (identity) => identity.identityId
  );

  findAll(): RTWOIdentity[] {
    return this.byId.findAll();
  }

  findById(identityId: IdentityId): RTWOIdentity | null {
    return this.byId.findById(identityId);
  }

  findByType(identityType: IdentityType): RTWOIdentity[] {
    return this.findAll().filter((identity) => identity.identityType === identityType);
  }

  save(identity: RTWOIdentity): void {
    this.byId.save(identity);
  }

  count(): number {
    return this.byId.count();
  }
}

export class KeyStoragePolicyRepository {
  private readonly byId = new InMemoryMapRepository<RTWOKeyStoragePolicy>(
    (policy) => policy.policyId
  );

  findAll(): RTWOKeyStoragePolicy[] {
    return this.byId.findAll();
  }

  findById(policyId: PolicyId): RTWOKeyStoragePolicy | null {
    return this.byId.findById(policyId);
  }

  save(policy: RTWOKeyStoragePolicy): void {
    this.byId.save(policy);
  }

  count(): number {
    return this.byId.count();
  }
}

export class TrustRecordRepository {
  private readonly byId = new InMemoryMapRepository<RTWOTrustRecord>(
    (record) => record.trustRecordId
  );

  findAll(): RTWOTrustRecord[] {
    return this.byId.findAll();
  }

  findById(trustRecordId: TrustRecordId): RTWOTrustRecord | null {
    return this.byId.findById(trustRecordId);
  }

  findByIdentity(identityId: IdentityId): RTWOTrustRecord[] {
    return this.findAll().filter((record) => record.identityId === identityId);
  }

  save(record: RTWOTrustRecord): void {
    this.byId.save(record);
  }

  count(): number {
    return this.byId.count();
  }
}

export class CryptoEventHistoryRepository {
  private events: CryptoEvent[] = [];

  add(event: CryptoEvent): void {
    this.events.unshift(event);
    if (this.events.length > 500) {
      this.events = this.events.slice(0, 500);
    }
  }

  findRecent(limit = 20): CryptoEvent[] {
    return this.events.slice(0, limit);
  }
}

export interface CryptoRepositories {
  keyPairs: KeyPairRepository;
  addresses: AddressRepository;
  signatures: SignatureRepository;
  identities: IdentityRepository;
  policies: KeyStoragePolicyRepository;
  trustRecords: TrustRecordRepository;
  eventHistory: CryptoEventHistoryRepository;
}

export function createCryptoRepositories(): CryptoRepositories {
  return {
    keyPairs: new KeyPairRepository(),
    addresses: new AddressRepository(),
    signatures: new SignatureRepository(),
    identities: new IdentityRepository(),
    policies: new KeyStoragePolicyRepository(),
    trustRecords: new TrustRecordRepository(),
    eventHistory: new CryptoEventHistoryRepository(),
  };
}
